import * as React from 'react';
import classNames from 'classnames';
import { useController } from 'react-hook-form';

import Icon from 'components/icon';

export interface RadioHighOption<T extends string | boolean> {
  value: T;
  label: string;
  description?: string;
  iconName?: string;
  imageUrl?: string;
  finishPreviewUrl?: string;
}

interface RadioHighGroupProps<T extends string | boolean> {
  name: string;
  label?: string;
  helpText?: React.ReactNode;
  options: ReadonlyArray<RadioHighOption<T>>;
  optionsClassName?: string;
  /** Hides the circular choice indicator from the `sm` breakpoint up (compact tiles). */
  compactChoice?: boolean;
  disabled?: boolean;
}

const optionInputId = (name: string, value: string | boolean) => `${name.replace(/\./g, '-')}-${String(value)}`;

/**
 * `c-form-element--radio c-form-element--high` option group from the
 * tempcontrol s3 cutup. Supports plain choice, icon figure, image, and
 * finish swatch heads.
 */
export function RadioHighGroup<T extends string | boolean>({
  name,
  label,
  helpText,
  options,
  optionsClassName,
  compactChoice = false,
  disabled,
}: RadioHighGroupProps<T>) {
  const fieldController = useController({ name });
  const { value, onChange, onBlur } = fieldController.field;
  const error = fieldController.fieldState.error;

  return (
    <div
      className={classNames('c-form-element c-form-element--radio c-form-element--high', {
        'c-form-element--error': typeof error !== 'undefined',
      })}
    >
      {typeof label === 'string' && label.length > 0 && (
        <label className="c-form-label c-form-label--cap">
          <strong>{label}</strong>
        </label>
      )}
      <div className={classNames('c-form-element__options', optionsClassName)}>
        {options.map((option) => {
          const inputId = optionInputId(name, option.value);
          const isSelected = value === option.value;

          return (
            <div key={inputId} className="c-form-element__field c-form-element__field--clean">
              <input
                type="radio"
                id={inputId}
                name={name}
                checked={isSelected}
                disabled={disabled}
                onChange={() => {
                  onChange(option.value);
                  onBlur();
                }}
              />
              <label htmlFor={inputId}>
                <OptionHead option={option} compactChoice={compactChoice} />
                <div className="c-form-option__body">
                  {option.label}
                  {typeof option.description === 'string' && option.description.length > 0 && (
                    <span>{option.description}</span>
                  )}
                </div>
              </label>
            </div>
          );
        })}
      </div>
      {typeof helpText !== 'undefined' && <p className="c-note">{helpText}</p>}
      {typeof error !== 'undefined' && (
        <ul className="c-form-element--error__list">
          <li>{error.message as string}</li>
        </ul>
      )}
    </div>
  );
}

const OptionHead = <T extends string | boolean>({
  option,
  compactChoice,
}: {
  option: RadioHighOption<T>;
  compactChoice: boolean;
}) => {
  if (option.finishPreviewUrl) {
    return (
      <div className="c-form-option__head">
        <ul className="c-finishes c-finishes--hor">
          <li>
            <span className="c-finishes__preview" style={{ backgroundImage: `url(${option.finishPreviewUrl})` }} />
          </li>
        </ul>
      </div>
    );
  }

  if (option.imageUrl) {
    return (
      <div className="c-form-option__figure c-form-option__head">
        <img src={option.imageUrl} alt="" />
      </div>
    );
  }

  if (option.iconName) {
    return (
      <div className="c-form-option__figure c-form-option__head">
        <Icon name={option.iconName} className="o-svg-icon" />
      </div>
    );
  }

  return (
    <div
      className={classNames('c-form-choice c-form-option__head', {
        'u-hidden@sm': compactChoice,
      })}
    />
  );
};

/** Row layout used for yes/no and icon-headed groups in s3. */
export const radioHighOptionsRowClassName =
  'c-form-element__options--inline@xs c-form-element__options--block@sm c-form-element__options--inline@md c-form-element__options--block@lg c-form-element__options--row@sm c-form-element__options--column@md c-form-element__options--row@lg';

/** Three-up grid for cabinet mount style cards. */
export const radioHighOptionsGridClassName =
  'c-form-element__options--inline@xs c-form-element__options--block@sm c-form-element__options--inline@md c-form-element__options--block@lg c-form-element__options--grid-3@sm c-form-element__options--grid-1@md c-form-element__options--grid-3@lg';

/** Vertical list with radio indicator on the left (facility type). */
export const radioHighOptionsListClassName =
  'c-form-element__options--inline@xs c-form-element__options--inline@sm c-form-element__options--inline@md c-form-element__options--inline@lg c-form-element__options--column@xs c-form-element__options--column@sm c-form-element__options--column@md c-form-element__options--column@lg';

/** Compact inline tiles for finish. */
export const radioHighOptionsCompactClassName =
  'c-form-element__options--inline@xs c-form-element__options--block@sm c-form-element__options--inline@md c-form-element__options--block@lg c-form-element__options--row@sm c-form-element__options--column@md c-form-element__options--row@lg c-form-element__options--align-center@sm c-form-element__options--align-left@md c-form-element__options--align-center@lg';

export default RadioHighGroup;
